import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
    FormInputComponent,
    InputComponentConfig,
    SharedModule,
} from '@vendure/admin-ui/core';

@Component({
    standalone: true,
    selector: 'relation-custom-field',
    imports: [CommonModule, SharedModule],
    template: `
        <vdr-form-field>
            <ng-container *ngIf="hasValue(); else placeholder">
                <ng-container *ngIf="isArray(value); else singleRelation">
                    <div class="chip-list">
                        <vdr-chip *ngFor="let item of value">
                            {{ formatLabel(item) }}
                        </vdr-chip>
                    </div>
                </ng-container>
                <ng-template #singleRelation>
                    <div class="relation-wrapper">
                        <img
                            *ngIf="isAsset(value)"
                            [src]="value.preview || value.source"
                            [alt]="formatLabel(value)"
                            class="relation-asset"
                        />
                        <span class="relation-label">{{
                            formatLabel(value)
                        }}</span>
                    </div>
                </ng-template>
            </ng-container>
            <ng-template #placeholder>
                <span class="relation-placeholder">—</span>
            </ng-template>
        </vdr-form-field>
    `,
    styles: [
        `
            .relation-wrapper {
                display: flex;
                align-items: center;
                gap: 0.5rem;
            }
            .relation-asset {
                width: 56px;
                height: 56px;
                border-radius: 4px;
                object-fit: cover;
                border: 1px solid rgba(0, 0, 0, 0.08);
            }
            .chip-list {
                display: flex;
                flex-wrap: wrap;
                gap: 0.25rem;
            }
            .relation-placeholder {
                color: var(--text-300);
            }
        `,
    ],
})
export class RelationCustomFieldComponent
    implements FormInputComponent<InputComponentConfig>
{
    @Input() readonly = false;
    @Input() formControl!: FormControl;
    @Input() config!: InputComponentConfig;

    get value(): any {
        return this.formControl?.value;
    }

    hasValue(): boolean {
        const value = this.value;
        if (value == null) {
            return false;
        }
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        if (typeof value === 'string') {
            return value.trim().length > 0;
        }
        return true;
    }

    isArray(value: any): value is any[] {
        return Array.isArray(value);
    }

    isAsset(value: any): boolean {
        return Boolean(value && (value.preview || value.source));
    }

    formatLabel(input: any): string {
        if (input == null) {
            return '—';
        }
        if (typeof input === 'string' || typeof input === 'number') {
            return String(input);
        }
        if (typeof input === 'boolean') {
            return input ? 'true' : 'false';
        }
        if (Array.isArray(input)) {
            return input.map((item) => this.formatLabel(item)).join(', ');
        }
        const fallbackKeys = [
            'label',
            'name',
            'title',
            'description',
            'code',
            'handle',
            'slug',
            'fileName',
            'email',
        ];
        for (const key of fallbackKeys) {
            const candidate = input[key];
            if (candidate != null && typeof candidate !== 'object') {
                return String(candidate);
            }
        }
        if (input.firstName || input.lastName) {
            return [input.firstName, input.lastName].filter(Boolean).join(' ');
        }
        if (input.customer || input.seller || input.channel) {
            const segments: string[] = [];
            if (input.seller) {
                segments.push(this.formatLabel(input.seller));
            }
            if (input.customer) {
                segments.push(this.formatLabel(input.customer));
            }
            if (input.channel) {
                segments.push(this.formatLabel(input.channel));
            }
            if (segments.length) {
                return segments.join(' · ');
            }
        }
        if (input.translations && Array.isArray(input.translations)) {
            const translation = input.translations[0];
            if (translation?.name) {
                return translation.name;
            }
        }
        if (input.id || input.id === 0) {
            return String(input.id);
        }
        try {
            return JSON.stringify(input);
        } catch {
            return String(input);
        }
    }
}
